import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AppTooltip from "../AppTooltip";
import HubPageHeader from "../HubPageHeader";
import {
  Eye,
  RefreshCw,
  Users,
  Activity,
  Clock,
  TrendingUp,
  AlertTriangle,
  Inbox,
  Tag as TagIcon,
} from "lucide-react";
import { format, isSameDay, parseISO } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const KpiPill = ({ icon, label, value, accent, isDark }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      px: 1.75,
      py: 1.25,
      borderRadius: 2.5,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
        color: "#fff",
        boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary", letterSpacing: -0.3, lineHeight: 1.2 }} noWrap>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const TicketRow = ({ ticket, isDark, onAccess, canAccess }) => {
  const updatedAt = ticket.updatedAt ? parseISO(ticket.updatedAt) : null;
  const isToday = updatedAt && isSameDay(updatedAt, new Date());
  const unread = Number(ticket.unreadMessages) > 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        cursor: canAccess ? "pointer" : "default",
        transition: "background .15s",
        "&:hover": canAccess ? { background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" } : {},
      }}
      onClick={canAccess ? onAccess : undefined}
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={unread ? Number(ticket.unreadMessages) : 0}
        color="error"
        max={99}
      >
        <Avatar src={ticket.contact?.urlPicture} sx={{ width: 36, height: 36 }}>
          {(ticket.contact?.name || "?")[0]}
        </Avatar>
      </Badge>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ticket.contact?.name || "—"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: unread ? "#2EA85A" : "text.secondary", fontWeight: unread ? 700 : 500 }}>
            {updatedAt ? (isToday ? format(updatedAt, "HH:mm") : format(updatedAt, "dd/MM/yy")) : ""}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.25 }}>
          {ticket.lastMessage || "—"}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          {ticket.whatsapp?.name && (
            <Chip
              size="small"
              label={ticket.whatsapp.name}
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 600,
                background: hexToRgba("#3b82f6", 0.15),
                color: "#3b82f6",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
          <Chip
            size="small"
            icon={<TagIcon size={10} style={{ marginLeft: 4 }} />}
            label={ticket.queue?.name || tLang("moments.noQueue", "Sem fila")}
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 600,
              background: ticket.queue?.color ? hexToRgba(ticket.queue.color, 0.18) : (isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)"),
              color: ticket.queue?.color || (isDark ? "#8696a0" : "#667781"),
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        </Stack>
      </Box>
      {canAccess && (
        <AppTooltip titleKey="moments.accessTicket">
          <IconButton
            size="small"
            sx={{
              borderRadius: "10px",
              color: "text.secondary",
              "&:hover": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onAccess?.();
            }}
          >
            <Eye size={16} />
          </IconButton>
        </AppTooltip>
      )}
    </Stack>
  );
};

const AgentCard = ({ agent, tickets, isDark, accent, headerLabel, headerIcon, onAccessTicket, canAccessFn, scrollbarStyles }) => {
  const total = tickets.length;
  const unreadTotal = tickets.reduce((acc, t) => acc + (Number(t.unreadMessages) || 0), 0);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        background: isDark ? "#11141b" : "#ffffff",
        border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.06)",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          p: 1.5,
          background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
          color: "#fff",
        }}
      >
        {agent ? (
          <Avatar
            src={agent.profileImage ? `${backendUrl}/public/company${agent.companyId || ""}/user/${agent.profileImage}` : null}
            sx={{ width: 40, height: 40, border: "2px solid rgba(255,255,255,0.3)" }}
          >
            {(agent.name || "?")[0]}
          </Avatar>
        ) : (
          <Avatar sx={{ width: 40, height: 40, bgcolor: "rgba(255,255,255,0.2)" }}>
            {headerIcon}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.2 }} noWrap>
            {agent?.name || headerLabel}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
            {total} {total === 1 ? tLang("moments.ticketSingular", "atendimento") : tLang("moments.ticketsPlural", "atendimentos")}
            {unreadTotal > 0 && ` · ${unreadTotal} ${tLang("moments.unread", "não lidas")}`}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          maxHeight: 360,
          p: 0.5,
          ...(scrollbarStyles || {}),
        }}
      >
        {tickets.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 3, color: "text.secondary" }}>
            <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>
              {tLang("moments.noTickets", "Nenhum atendimento ativo")}
            </Typography>
          </Stack>
        ) : (
          tickets.map((ticket) => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              isDark={isDark}
              onAccess={() => onAccessTicket(ticket)}
              canAccess={canAccessFn(ticket)}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

const MomentsUser = () => {
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const themeV4 = useThemeV4();
  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const tokens = useMemo(
    () => ({
      textPrimary: isDark ? "#e9edef" : "#0f172a",
      textSecondary: isDark ? "#8696a0" : "#64748b",
    }),
    [isDark]
  );

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const companyId = user.companyId;

  const fetchData = async () => {
    try {
      const { data } = await api.get("/usersMoments");
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status !== 500) {
        toastError(err);
      } else {
        toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket || !companyId) return;
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchData();
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId]);

  const grouped = useMemo(() => {
    const byUser = [];
    const pending = [];
    tickets.forEach((t) => {
      if (!t.user) {
        pending.push(t);
      } else {
        const idx = byUser.findIndex((g) => g.user.id === t.user.id);
        if (idx === -1) {
          byUser.push({ user: { ...t.user, companyId }, tickets: [t] });
        } else {
          byUser[idx].tickets.push(t);
        }
      }
    });
    byUser.sort((a, b) => b.tickets.length - a.tickets.length);
    return { byUser, pending };
  }, [tickets, companyId]);

  const stats = useMemo(() => {
    const totalActive = tickets.filter((t) => t.user).length;
    const totalPending = grouped.pending.length;
    const onlineAgents = grouped.byUser.length;
    const top = grouped.byUser[0];
    return { totalActive, totalPending, onlineAgents, top };
  }, [tickets, grouped]);

  const canAccessTicket = (ticket) => user.profile === "admin" || ticket.userId === user.id;
  const accessTicket = (ticket) => history.push(`/tickets/${ticket.uuid}`);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}>
        <CircularProgress sx={{ color: PRIMARY_MAIN }} />
      </Stack>
    );
  }

  return (
    <Box>
      <HubPageHeader
        icon={<Activity size={20} strokeWidth={1.8} />}
        title={tLang("moments.title", "Painel em tempo real")}
        subtitle={tLang("moments.subtitle", "Atendimentos ao vivo agrupados por atendente")}
        action={
          <AppTooltip title={tLang("moments.refresh", "Atualizar")}>
            <IconButton
              onClick={fetchData}
              sx={{
                background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
                color: "#fff",
                borderRadius: "12px",
                boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                  transform: "translateY(-1px)",
                  boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
                },
              }}
            >
              <RefreshCw size={18} />
            </IconButton>
          </AppTooltip>
        }
      />

      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={3}>
          <KpiPill
            icon={<Users size={18} />}
            label={tLang("moments.kpis.online", "Atendentes online")}
            value={stats.onlineAgents}
            accent={PRIMARY_MAIN}
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiPill
            icon={<Activity size={18} />}
            label={tLang("moments.kpis.active", "Em atendimento")}
            value={stats.totalActive}
            accent="#2EA85A"
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiPill
            icon={<Clock size={18} />}
            label={tLang("moments.kpis.pending", "Aguardando")}
            value={stats.totalPending}
            accent="#F7C82C"
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiPill
            icon={<TrendingUp size={18} />}
            label={tLang("moments.kpis.top", "Top atendente")}
            value={stats.top ? `${stats.top.user.name.split(" ")[0]} (${stats.top.tickets.length})` : "—"}
            accent="#8b5cf6"
            isDark={isDark}
          />
        </Grid>
      </Grid>

      {/* GRID DE CARDS */}
      {grouped.byUser.length === 0 && grouped.pending.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{
            py: 8,
            borderRadius: 3,
            background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
            border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.05)",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: hexToRgba(PRIMARY_MAIN, isDark ? 0.15 : 0.10),
              color: PRIMARY_MAIN,
            }}
          >
            <Inbox size={28} />
          </Box>
          <Typography sx={{ fontSize: 13, color: tokens.textSecondary, textAlign: "center", maxWidth: 320 }}>
            {tLang("moments.empty", "Nenhum atendimento ativo no momento.")}
          </Typography>
        </Stack>
      ) : (
        <Grid container spacing={1.5}>
          {grouped.pending.length > 0 && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <AgentCard
                agent={null}
                tickets={grouped.pending}
                isDark={isDark}
                accent="#F7C82C"
                headerLabel={tLang("moments.pending", "Pendentes")}
                headerIcon={<AlertTriangle size={20} color="#fff" />}
                onAccessTicket={accessTicket}
                canAccessFn={canAccessTicket}
                scrollbarStyles={themeV4?.scrollbarStyles}
              />
            </Grid>
          )}
          {grouped.byUser.map((group) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={group.user.id}>
              <AgentCard
                agent={group.user}
                tickets={group.tickets}
                isDark={isDark}
                accent={PRIMARY_MAIN}
                onAccessTicket={accessTicket}
                canAccessFn={canAccessTicket}
                scrollbarStyles={themeV4?.scrollbarStyles}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MomentsUser;
